<template>
  <nav
    class="navbar is-fixed-top"
    role="navigation"
    aria-label="main navigation"
  >
    <div class="navbar-brand">
      <div
        class="navbar-item webcom-logo is-flex-desktop"
        :class="{'is-visible': !isMiniSidebar}"
      >
        <img
          v-if="$store.state.logos.length"
          :src="$store.state.logos[1]"
        >
        <transition
          v-else
          name="fade"
          mode="out-in"
          @before-leave="transitioning = true"
          @after-leave="transitioning = false"
        >
          <img
            v-if="isMiniSidebar && !transitioning"
            :key="1"
            src="../../src/assets/responsive_logo_white.png"
          >
          <img
            v-if="!isMiniSidebar && !transitioning"
            :key="2"
            alt="web.com"
            class="navbar-logo"
            src="../../src/assets/white-logo.png"
          >
        </transition>
      </div>
      <a
        role="button"
        class="navbar-burger"
        :class="{ 'is-active': isSidebarVisible }"
        aria-label="menu"
        aria-expanded="false"
        @click="$emit('toggleSidebar')"
      >
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </a>

      <div
        v-show="hasMultipleLang"
        class="dropdown multiple-lang is-right navbar-item"
        :class="{ 'is-active': openLang }"
        @click="openLang = !openLang"
        @mouseleave="langLostFocus"
      >
        <div class="dropdown-trigger">
          <div class="columns is-gapless is-mobile is-vcentered">
            <div class="column is-4 ">
              <country-flag
                :country="langSelected.flag"
                size="small"
              />
            </div>
            <div class="column">
              <span class="lang-text">
                {{ $t('language.dropdown.'+langSelected.language) }}
              </span>
            </div>
          </div>
        </div>
        <div
          id="dropdown-menu"
          class="dropdown-menu"
          role="menu"
        >
          <div class="dropdown-content">
            <a
              v-for="lang in langContent"
              :key="lang.title"
              class="dropdown-item lang-dropdown"
              @click="loadLanguage(lang.language)"
            >
              <div class="columns is-gapless is-mobile is-vcentered">
                <div class="column is-4">
                  <country-flag
                    :country="lang.flag"
                    size="small"
                  />
                </div>
                <div class="column column-text">
                  <span class="lang-text">
                    {{ $t('language.dropdown.'+lang.language) }}
                  </span>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <div
        class="dropdown user-menu is-right navbar-item clogclass"
        :class="{ 'is-active': openSetup }"
        @click="openSetup = !openSetup"
        @mouseleave="setupLostFocus"
        @mouseenter="mOnSetup"
      >
        <div class="dropdown-trigger">
          <span
            class="username-bg"
            aria-haspopup="true"
            aria-controls="dropdown-menu"
          >
            <span class="user">
              <font-awesome-icon icon="cog" />
            </span>
          </span>
        </div>
        <div
          id="dropdown-menu"
          class="dropdown-menu"
          role="menu"
        >
          <div class="dropdown-content">
            <router-link
              v-if="(accountCount > 1)"
              :to="{ name: 'Accounts' }"
              class="dropdown-item"
            >
              {{ $t('common.switch_account') }}
            </router-link>
            <a
              class="dropdown-item"
              @click.prevent="signOut"
            >
              {{ $t('common.sign_out') }}
            </a>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import tenantConfigMixin from '@/mixins/tenant-config'

export default {
  name: 'IntlNavbar',
  mixins: [tenantConfigMixin],
  props: {
    isSidebarVisible: Boolean,
    isMiniSidebar: Boolean,
    isClosedOnMobile: Boolean
  },
  data () {
    return {
      transitioning: false,
      languages: [],
      langActive: '',
      setupActive: '',
      openSetup: false,
      openLang: false,
      mouseOnSetup: false
    }
  },
  computed: {
    ...mapGetters(['username', 'preferredLanguage', 'accountCount']),
    langContent () {
      return this.languages.filter(lang => lang.language !== this.preferredLanguage)
    },
    langSelected () {
      const result = this.languages.filter(lang => lang.language === this.preferredLanguage)

      if (typeof result[0] !== 'undefined') {
        return result[0]
      }
      return { flag: '', language: '', title: '' }
    },
    hasMultipleLang () {
      if (this.languages.length > 1) {
        return true
      }
      return false
    }
  },
  mounted () {
    this.languages = this.getTenantConfig('languages')
  },
  methods: {
    ...mapActions(['handleSession', 'loadLanguage', 'clearSessionData']),
    async signOut () {
      await this.$auth.signOut()
      this.handleSession(null)
      this.clearSessionData()
      this.$router.history.push('/')
    },
    setupLostFocus () {
      this.mouseOnSetup = false
      setTimeout(() => {
        if (!this.mouseOnSetup) {
          this.openSetup = false
        }
      }, 1000)
    },
    mOnSetup () {
      this.mouseOnSetup = true
    },
    langLostFocus () {
      this.openLang = false
    },
    openLanguage () {
      this.mouseOnSetup = false
      this.openSetup = false
      this.openLang = true
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../sass/_variables.scss';
.user {
  .fa-cog {
    font-size: 16px;
  }
}
.navbar {
  padding-right: 15px;
  background-color: var(--navbar-color);
  z-index: 300;
}

.clogclass {
  padding-right:5px;
  padding-left:15px;
}

.dropdown-flag {
  background-color:#FFF;
  color:#000;
}
.navbar-logo {
  max-width: 100px;
}

.navbar-burger {
  min-height: $navbar-height;
  min-width: 50px;
  margin-left: inherit;
  margin-right: auto;

  &:hover {
    color: white;
  }
}

.navbar-brand {
  min-height: $navbar-height;
  min-width: 50px;
}

.webcom-logo {
  display: none;
  background-color: var(--navbar-logo-color);
  width: $sidebar-icon-width;
  transition: width $animation-fast;

  img {
    max-height: 2em;
    margin: auto;
  }

  &.is-visible {
    width: $sidebar-width;
  }
}

.navbar-item {
  cursor: pointer;
  color: var(--navbar-text-color);
  padding: 5px 18px 5px 2px;
}
.dropdown-content {
  border-radius: 0px;
  padding-left: 20px;
  padding-right: 35px;

  .dropdown-item {
    font-size: 12px;
  }
}

.dropdown-trigger {
  font-size: 12px;
}

.lang-text {
  padding-bottom: 4px;
  display: block;
}
.lang-dropdown {
  padding: 0px 0px 0px 0px !important;
}
.user-menu {
  padding-right: 10px;
}

</style>
